import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { ArrowUndo20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import { DeleteImportedConsumptionsResult } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { UndoImportTarget } from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ErrorType } from '../../../models/HttpError';
import '../ProviderConsumptionImport/ProviderConsumptionImport.Styles.css';
import './ProviderConsumptions.Styles.css';

export interface UndoImportDialogProps {
  /** Archivo a deshacer; sin target el diálogo está cerrado. */
  target: UndoImportTarget | undefined;
  /** DELETE providers/{int}/files/{guid}/imported-consumptions (thunk existente; todo o nada, 409). */
  onUndo: (fileId: string) => Promise<DeleteImportedConsumptionsResult | undefined>;
  onDismiss: () => void;
  /** Se deshizo (200): la sección recarga grilla, KPI, barra y archivos. */
  onUndone?: (target: UndoImportTarget, result: DeleteImportedConsumptionsResult | undefined) => void;
}

type Phase = 'confirm' | 'running' | 'done' | 'error';

const isConflict = (error: unknown): boolean => Number((error as Partial<ErrorType> | undefined)?.ErrorCode) === 409;

/**
 * "Deshacer importación" de un archivo del proveedor (spec 010, US7): confirmación con el archivo y la cantidad,
 * ejecución, resultado (`deleted`) y el motivo del 409, todo en el mismo diálogo. Los textos del servidor van como texto.
 */
export const UndoImportDialog = ({ target, onUndo, onDismiss, onUndone }: UndoImportDialogProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.undo.${path}`, options);
  const [phase, setPhase] = useState<Phase>('confirm');
  const [deleted, setDeleted] = useState(0);
  const [error, setError] = useState<unknown>(undefined);

  const targetKey = target ? `${target.fileId}|${target.count ?? ''}` : '';
  useEffect(() => {
    setPhase('confirm');
    setDeleted(0);
    setError(undefined);
  }, [targetKey]);

  if (!target) return null;
  const running = phase === 'running';
  const conflict = phase === 'error' && isConflict(error);

  const run = async () => {
    setPhase('running');
    setError(undefined);
    try {
      const result = await onUndo(target.fileId);
      setDeleted(Number(result?.deleted ?? 0));
      setPhase('done');
      onUndone?.(target, result);
    } catch (failure: unknown) {
      setError(failure);
      setPhase('error');
    }
  };

  const close = () => {
    if (!running) onDismiss();
  };

  const renderBody = () => {
    if (phase === 'done') {
      return (
        <GuideMessageBar intent="success" layout="multiline" data-testid="pcs-undo-result">
          <MessageBarBody>
            <MessageBarTitle>{tr('doneTitle')}</MessageBarTitle>
            {deleted > 0 ? tr('done', { count: deleted, name: target.fileName }) : tr('doneNone', { name: target.fileName })}
          </MessageBarBody>
        </GuideMessageBar>
      );
    }
    return (
      <>
        <p>{target.count !== undefined ? tr('confirmText', { count: target.count, name: target.fileName }) : tr('confirmTextNoCount', { name: target.fileName })}</p>
        <p className="pcs-secondary">{tr('warning')}</p>
        {phase === 'error' && (
          <GuideMessageBar intent={conflict ? 'warning' : 'error'} layout="multiline" data-testid="pcs-undo-error">
            <MessageBarBody>
              <MessageBarTitle>{conflict ? tr('conflictTitle') : tr('errorTitle')}</MessageBarTitle>
              <span className="pcs-undo__message">{getApiErrorMessage(error)}</span>
              {conflict && <span className="pcs-secondary">{tr('conflictHelp')}</span>}
            </MessageBarBody>
          </GuideMessageBar>
        )}
      </>
    );
  };

  const renderActions = () => {
    if (phase === 'done') {
      return (
        <Button appearance="primary" onClick={onDismiss} data-testid="pcs-undo-close">
          {tr('close')}
        </Button>
      );
    }
    if (conflict) {
      return (
        <Button appearance="primary" onClick={onDismiss} data-testid="pcs-undo-close">
          {tr('close')}
        </Button>
      );
    }
    return (
      <>
        <Button appearance="secondary" onClick={close} disabled={running} data-testid="pcs-undo-cancel">
          {tr('cancel')}
        </Button>
        <Button
          appearance="secondary"
          className="pci-danger"
          icon={<ArrowUndo20Regular />}
          onClick={run}
          disabled={running}
          aria-busy={running || undefined}
          data-testid={phase === 'error' ? 'pcs-undo-retry' : 'pcs-undo-confirm'}
        >
          {running ? tr('running') : phase === 'error' ? tr('retry') : tr('confirm')}
        </Button>
      </>
    );
  };

  return (
    <Dialog open modalType="alert" onOpenChange={(_event, data) => !data.open && close()}>
      <DialogSurface className="pcs-undo" data-testid="pcs-undo-dialog">
        <DialogBody>
          <DialogTitle>{tr('title')}</DialogTitle>
          <DialogContent className="pcs-undo__content">{renderBody()}</DialogContent>
          <DialogActions>{renderActions()}</DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UndoImportDialog;
